<script setup>
import { computed, onMounted, ref, defineEmits } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import * as persianTools from "@persian-tools/persian-tools"
import PersianDate from "persian-date"

import store from "../../state/store"
import useOrders from "../../use/useOrders"
import useDebouncedFunction from "../../use/useDebouncedFunction"

import TableHeaderCell from "@/components/table/TableHeaderCell.vue"

const orders = computed(() => store.state.orders)

const {
  orderStatus,
  search,
  perPage,
  sortField,
  sortDirection,
  getOrders,
  sortOrders,
} = useOrders()
const { handleInput } = useDebouncedFunction(getOrders)

const formattedPrices = computed(() => {
  return orders.value.data.map((order) =>
    new Intl.NumberFormat("fa-IR").format(order.totalPrice)
  )
})

const emit = defineEmits(["clickEdit"])

onMounted(() => {
  getOrders()
})

const statusLabels = {
  PAYING: "در حال پرداخت",
  PENDING: "در انتظار تایید (در حال بررسی)",
  PREPARING_TO_SEND: "آماده سازی برای ارسال",
  DELIVERED: "تحویل داده شده",
  CANCELLED: "لغو شده",
}

const toPersianDateTime = (date) => {
  const [datePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const gregorianDate = new Date(year, month - 1, day)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  getOrders(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})

const editOrder = (ord) => {
  emit("clickEdit", ord)
}
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="7">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی شماره سفارش ..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="5">
        <b-row class="g-3">
          <b-col sm="6">
            <select
              @change="getOrders(null)"
              v-model="orderStatus"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>انتخاب وضعیت سفارش</option>
              <option
                v-for="(label, value) in statusLabels"
                :key="value"
                :value="value"
              >
                {{ label }}
              </option>
            </select>
          </b-col>

          <b-col sm="6">
            <select
              @change="getOrders(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>نمایش تعداد ردیف</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="orderTable">
          <thead class="table-light text-muted">
            <tr>
              <TableHeaderCell
                field="tracking_code"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOrders('tracking_code')"
              >
                شماره سفارش
              </TableHeaderCell>

              <th class="sort" data-sort="user">نام مشتری</th>

              <TableHeaderCell
                field="delivery_time"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOrders('delivery_time')"
              >
                تاریخ دریافت سفارش
              </TableHeaderCell>
              <th class="sort" data-sort="timeRange">بازه زمانی</th>

              <TableHeaderCell
                field="total_price"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOrders('total_price')"
              >
                مبلغ
              </TableHeaderCell>

              <TableHeaderCell
                field="order_status"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOrders('order_status')"
              >
                وضعیت
              </TableHeaderCell>

              <TableHeaderCell
                field="created_at"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOrders('created_at')"
              >
                تاریخ ثبت سفارش
              </TableHeaderCell>

              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>
          <tbody class="list" v-if="orders.loading || !orders.data.length">
            <tr>
              <td colspan="8">
                <div class="text-center">
                  <b-spinner
                    v-if="orders.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ سفارشی وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(order, index) of orders.data" :key="index">
              <td class="trackingCode">
                {{ persianTools.digitsEnToFa(order.trackingCode) }}
              </td>
              <td class="user">{{ order.user }}</td>
              <td class="deliveryTime">
                {{ toPersianDateTime(order.deliveryTime) }}
              </td>
              <td class="timeRange">
                {{ order.timeRange }}
              </td>
              <td class="totalPrice">{{ formattedPrices[index] }}</td>
              <td class="status">
                <span
                  class="badge text-uppercase"
                  :class="{
                    'badge-soft-warning': order.status == 'PAYING',
                    'badge-soft-info': order.status == 'PENDING',
                    'badge-soft-primary': order.status == 'PREPARING_TO_SEND',
                    'badge-soft-success': order.status == 'DELIVERED',
                    'badge-soft-danger': order.status == 'CANCELLED',
                  }"
                  >{{ statusLabels[order.status] }}</span
                >
              </td>
              <td class="dateTime">
                {{ toPersianDateTime(order.dateTime) }}
              </td>
              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    class="list-inline-item"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="مشاهده جزئیات سفارش"
                  >
                    <RouterLink
                      :to="{
                        name: 'orders.details',
                        params: { id: order.id },
                      }"
                      class="text-primary d-inline-block"
                      target="_blank"
                      ><i class="ri-eye-fill fs-16"></i>
                    </RouterLink>
                  </li>
                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Edit"
                  >
                    <b-link
                      variant="text-primary"
                      class="d-inline-block edit-item-btn"
                      @click="editOrder(order)"
                    >
                      <i class="ri-pencil-fill fs-16"></i>
                    </b-link>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="d-flex justify-content-end"
        v-if="orders.total > orders.limit"
      >
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of orders.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>
